import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import AccessGate, { SignOutWatcher, FullLoader } from "@/components/auth/AccessGate";
import { ProgressProvider } from "@/modules/premiere-video/store";
import AppLayout, { ScrollToTop } from "@/components/AppLayout";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import MissionPage from "@/pages/MissionPage";
import Results from "@/pages/Results";
import Resources from "@/pages/Resources";
import FaqPage from "@/pages/FaqPage";
import Access from "@/pages/Access";
import GiftPage from "@/pages/Gift";
import Certificate from "@/pages/Certificate";
import Admin from "@/pages/Admin";
import Rehearse from "@/pages/Rehearse";
import ScriptPrint from "@/pages/ScriptPrint";
import { SignInPage, SignUpPage } from "@/pages/AuthPages";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import PlatformLayout from "@/components/platform/PlatformLayout";
import PlatformHome from "@/pages/platform/PlatformHome";
import { ApplicationsPage, ProductPage, SpacePage, LegalPage } from "@/pages/platform/PublicPages";
import { MySpacePage, MyAccountPage } from "@/pages/platform/MemberPages";
import PlatformAdmin from "@/pages/platform/PlatformAdmin";

// Public pages render without Clerk, so they stay available even if sign-in is down.
const publicRoutes = () => [
  <Route key="pl" element={<PlatformLayout />}>
    <Route path="/" element={<PlatformHome />} />
    <Route path="/applications" element={<ApplicationsPage />} />
    <Route path="/applications/:slug" element={<ProductPage />} />
    <Route path="/boite-a-outils" element={<SpacePage space="boite-a-outils" />} />
    <Route path="/le-cercle" element={<SpacePage space="le-cercle" />} />
    <Route path="/academie" element={<SpacePage space="academie" />} />
    <Route path="/mentions-legales" element={<LegalPage page="mentions-legales" />} />
    <Route path="/cgv" element={<LegalPage page="cgv" />} />
    <Route path="/confidentialite" element={<LegalPage page="confidentialite" />} />
  </Route>,
  <Route key="ugcgo" path="/ugc-go" element={<Home />} />,
];

function AuthFallback(unavailable) {
  return (
    <Routes>
      {publicRoutes()}
      <Route path="*" element={unavailable ? <AuthUnavailable /> : <FullLoader />} />
    </Routes>
  );
}

function AuthUnavailable() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-ivory px-6 text-center" data-testid="auth-unavailable">
      <p className="font-display text-2xl text-bx">La connexion est momentanément indisponible.</p>
      <p className="text-soft">Actualise la page dans quelques instants pour réessayer.</p>
      <a href="/" className="mt-2 text-sm text-bx underline-offset-4 hover:underline" data-testid="auth-unavailable-home">Retour à l'accueil</a>
    </div>
  );
}

function App() {
  return (
    <ProgressProvider>
      <BrowserRouter>
        <ClerkAuthProvider fallback={AuthFallback}>
          <ScrollToTop />
          <SignOutWatcher />
          <Routes>
            {publicRoutes()}
            <Route element={<PlatformLayout />}>
              <Route path="/mon-espace" element={<MySpacePage />} />
              <Route path="/mon-compte" element={<MyAccountPage />} />
              <Route path="/administration" element={<PlatformAdmin />} />
            </Route>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route path="/acces" element={<Access />} />
            <Route path="/offrir" element={<GiftPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route element={<AccessGate />}>
              <Route element={<AppLayout />}>
                <Route path="/parcours" element={<Dashboard />} />
                <Route path="/mission/:id" element={<MissionPage />} />
                <Route path="/resultats" element={<Results />} />
                <Route path="/ressources" element={<Resources />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/admin" element={<Admin />} />
              </Route>
              <Route path="/script/imprimer" element={<ScriptPrint />} />
              <Route path="/script/repeter" element={<Rehearse />} />
              <Route path="/certificat" element={<Certificate />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ClerkAuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" toastOptions={{ style: { fontFamily: "DM Sans, sans-serif", borderRadius: 16, border: "1px solid #EBE5DF" } }} />
    </ProgressProvider>
  );
}

export default App;
